import type { FileUrlDescriptor } from '@magicdoorinc/file';
import type { FileUrls } from '~/use-cases/types/FileUrls';

export interface Unit {
  id: string;
}

export interface CompanyUnit {
  availableFrom?: Date;
  baths?: number;
  beds?: number;
  id?: string;
  leaseTerm?: LeaseTerm;
  listed?: Date;
  pinned?: boolean;
  primaryImage?: EmbeddedFile;
  primaryVideo?: EmbeddedFile;
  propertyType?: PropertyType;
  rentalPrice?: number;
  singleLineAddress?: string;
  title?: string;
  unitSizeSqft?: number;
}

export interface EmbeddedFile {
  default?: boolean;
  description?: string;
  fileId?: string;
  fileUrl?: FileUrlDescriptor;
  urls?: FileUrls;
}

export enum PropertyType {
  SINGLE_FAMILY = 'singleFamily',
  MULTI_FAMILY = 'multiFamily',
  COMMERCIAL = 'commercial',
  CONDO = 'condo',
  TOWNHOME = 'townHome',
  OTHER = 'other',
}

export interface UnitParams {
  availableFrom?: string;
  maxRentalPrice?: number;
  minBaths?: number;
  minBeds?: number;
  minRentalPrice?: number;
  page?: number;
  pageSize?: number;
  pinned?: string;
  propertyTypes?: PropertyType[];
  search?: string;
}

export interface PaginatedUnits<T = CompanyUnit> {
  currentPage: number;
  items: T[];
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface TenantUnitListing {
  amenities?: Amenity[];
  availableFrom?: Date;
  baths?: number;
  beds?: number;
  description?: string;
  descriptionTranslations?: UnitDescriptionTranslation[];
  id?: string;
  images?: EmbeddedFile[];
  leaseTerm?: LeaseTerm;
  listed?: Date;
  locationAnalysis?: string;
  petPolicy?: PetPolicy;
  pinned?: boolean;
  primaryImage?: EmbeddedFile;
  primaryVideo?: EmbeddedFile;
  propertyType?: PropertyType;
  rentalPrice?: number;
  securityDeposit?: number;
  singleLineAddress?: string;
  tags?: string[];
  terms?: Record<string, string>;
  title?: string;
  unitSizeSqft?: number;
  updated?: Date;
  videos?: EmbeddedFile[];
}

export interface Amenity {
  avalability?: AmenityAvalability;
  description?: string;
  icon?: string;
  name?: string;
  type?: AmenityType;
}

export enum AmenityAvalability {
  Available = 'available',
  Limited = 'limited',
  Pending = 'pending',
  Unavailable = 'unavailable',
}

export enum AmenityType {
  Community = 'community',
  Outdoor = 'outdoor',
  Utility = 'utility',
  Interior = 'interior',
}

export interface UnitDescriptionTranslation {
  language?: string;
  value?: string;
}

export enum LeaseTerm {
  HalfYear = 'halfYear',
  MonthToMonth = 'monthToMonth',
  OneYear = 'oneYear',
  Other = 'other',
  ThreeYears = 'threeYears',
  TwoYears = 'twoYears',
}

export interface AllowedPetTypes {
  cat?: boolean;
  dog?: boolean;
  fish?: boolean;
  bird?: boolean;
  reptile?: boolean;
  other?: boolean;
}

export enum PetSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export interface PetPolicy {
  allowed?: boolean;
  allowedPetTypes?: AllowedPetTypes;
  size?: PetSize;
  fee?: number;
  deposit?: number;
  description?: string;
}
